import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Upload, FileText, Award, Image, Globe, 
  Mail, Phone, MapPin, Users, CheckCircle, Eye, 
  Plus, Trash2, Edit, Save, X, Star, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorShowcasePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    tagline: '',
    description: '',
    year_founded: '',
    employee_count: '',
    headquarters_location: '',
    service_areas: [],
    core_competencies: [],
    contact_email: '',
    contact_phone: '',
    website: ''
  });
  const [newCompetency, setNewCompetency] = useState('');
  const [newServiceArea, setNewServiceArea] = useState('');
  const [newCert, setNewCert] = useState({ name: '', issuing_body: '', expiry_date: '' });

  const token = localStorage.getItem('supplierToken') || localStorage.getItem('token');

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/vendor-showcase/my-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setProfile(data);
      setFormData({
        company_name: data.company_name || '',
        tagline: data.tagline || '',
        description: data.description || '',
        year_founded: data.year_founded || '',
        employee_count: data.employee_count || '',
        headquarters_location: data.headquarters_location || '',
        service_areas: data.service_areas || [],
        core_competencies: data.core_competencies || [],
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        website: data.website || ''
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/vendor-showcase/my-profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      await fetchProfile();
      setEditMode(false);
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const publishProfile = async () => {
    try {
      const res = await fetch(`${API}/api/vendor-showcase/my-profile/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('Profile published successfully!');
        fetchProfile();
      } else {
        alert(data.detail || 'Failed to publish profile');
      }
    } catch (err) {
      console.error('Error publishing:', err);
    }
  };

  const uploadFile = async (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'logo' ? 'logo' : type === 'header' ? 'header-image' : 'capability-statement';
      const res = await fetch(`${API}/api/vendor-showcase/my-profile/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchProfile();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
    }
  };

  const addCertification = async () => {
    if (!newCert.name) return;
    try {
      await fetch(`${API}/api/vendor-showcase/my-profile/certifications`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCert)
      });
      setNewCert({ name: '', issuing_body: '', expiry_date: '' });
      fetchProfile();
    } catch (err) {
      console.error('Error adding certification:', err);
    }
  };

  const removeCertification = async (certId) => {
    try {
      await fetch(`${API}/api/vendor-showcase/my-profile/certifications/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProfile();
    } catch (err) {
      console.error('Error removing certification:', err);
    }
  };

  const addCompetency = () => {
    if (newCompetency && !formData.core_competencies.includes(newCompetency)) {
      setFormData({
        ...formData,
        core_competencies: [...formData.core_competencies, newCompetency]
      });
      setNewCompetency('');
    }
  };

  const removeCompetency = (comp) => {
    setFormData({
      ...formData,
      core_competencies: formData.core_competencies.filter(c => c !== comp)
    });
  };

  const addServiceArea = () => {
    if (newServiceArea && !formData.service_areas.includes(newServiceArea)) {
      setFormData({
        ...formData,
        service_areas: [...formData.service_areas, newServiceArea]
      });
      setNewServiceArea('');
    }
  };

  const removeServiceArea = (area) => {
    setFormData({
      ...formData,
      service_areas: formData.service_areas.filter(a => a !== area)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const completeness = profile?.profile_completeness || 0;

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="vendor-showcase-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-600 rounded-xl">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Vendor Showcase</h1>
              <p className="text-gray-600">Build your professional profile for hospital decision-makers</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {profile?.is_published ? (
              <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                <Eye className="w-4 h-4" /> Published
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <X className="w-4 h-4" /> Not Published
              </Badge>
            )}
            {!profile?.is_published && completeness >= 50 && (
              <Button onClick={publishProfile} className="bg-green-600 hover:bg-green-700">
                Publish Profile
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Profile Completeness */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Profile Completeness</span>
            <span className="text-sm font-bold">{completeness}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${completeness >= 50 ? 'bg-green-500' : 'bg-yellow-500'}`}
              style={{ width: `${completeness}%` }}
            />
          </div>
          {completeness < 50 && (
            <p className="text-xs text-gray-500 mt-2">
              Complete at least 50% to publish your profile
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="profile">Company Profile</TabsTrigger>
          <TabsTrigger value="branding">Branding & Files</TabsTrigger>
          <TabsTrigger value="certifications">Certifications</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
        </TabsList>

        {/* Company Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Company Information</CardTitle>
                  <CardDescription>Details that help hospitals understand your capabilities</CardDescription>
                </div>
                {!editMode ? (
                  <Button onClick={() => setEditMode(true)} variant="outline">
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={() => setEditMode(false)} variant="ghost">Cancel</Button>
                    <Button onClick={saveProfile} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name *</label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tagline</label>
                  <Input 
                    value={formData.tagline}
                    onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                    disabled={!editMode}
                    placeholder="Your company slogan or specialty"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea 
                    className="w-full border rounded-md p-3 min-h-[100px]"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    disabled={!editMode}
                    placeholder="Describe your company and services..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Year Founded</label>
                  <Input 
                    type="number"
                    value={formData.year_founded}
                    onChange={(e) => setFormData({...formData, year_founded: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Employee Count</label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({...formData, employee_count: e.target.value})}
                    disabled={!editMode}
                  >
                    <option value="">Select...</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="200+">200+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Headquarters</label>
                  <Input 
                    value={formData.headquarters_location}
                    onChange={(e) => setFormData({...formData, headquarters_location: e.target.value})}
                    disabled={!editMode}
                    placeholder="City, State"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <Input 
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    disabled={!editMode}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Email</label>
                  <Input 
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Phone</label>
                  <Input 
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                {/* Core Competencies */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Core Competencies</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.core_competencies.map((comp, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1">
                        {comp}
                        {editMode && (
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeCompetency(comp)} />
                        )}
                      </Badge>
                    ))}
                  </div>
                  {editMode && (
                    <div className="flex gap-2">
                      <Input 
                        value={newCompetency}
                        onChange={(e) => setNewCompetency(e.target.value)}
                        placeholder="e.g., Cryogenic Transport, Emergency HVAC"
                        onKeyPress={(e) => e.key === 'Enter' && addCompetency()}
                      />
                      <Button type="button" onClick={addCompetency} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Service Areas */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Service Areas</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.service_areas.map((area, i) => (
                      <Badge key={i} variant="outline" className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {area}
                        {editMode && (
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeServiceArea(area)} />
                        )}
                      </Badge>
                    ))}
                  </div>
                  {editMode && (
                    <div className="flex gap-2">
                      <Input 
                        value={newServiceArea}
                        onChange={(e) => setNewServiceArea(e.target.value)}
                        placeholder="e.g., Houston, Dallas, San Antonio"
                        onKeyPress={(e) => e.key === 'Enter' && addServiceArea()}
                      />
                      <Button type="button" onClick={addServiceArea} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" /> Company Logo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.logo_url ? (
                  <div className="mb-4">
                    <img 
                      src={`${API}${profile.logo_url}`} 
                      alt="Logo" 
                      className="max-h-32 object-contain"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400 mb-4">
                    No logo uploaded
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && uploadFile('logo', e.target.files[0])}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button asChild className="w-full">
                    <span><Upload className="w-4 h-4 mr-2" /> Upload Logo</span>
                  </Button>
                </label>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" /> Header Banner
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.header_image_url ? (
                  <div className="mb-4">
                    <img 
                      src={`${API}${profile.header_image_url}`} 
                      alt="Header" 
                      className="w-full h-32 object-cover rounded"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400 mb-4">
                    No header image uploaded
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && uploadFile('header', e.target.files[0])}
                  className="hidden"
                  id="header-upload"
                />
                <label htmlFor="header-upload">
                  <Button asChild className="w-full">
                    <span><Upload className="w-4 h-4 mr-2" /> Upload Header</span>
                  </Button>
                </label>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Capability Statement (PDF)
                </CardTitle>
                <CardDescription>
                  Upload your standard capability statement document for hospital procurement
                </CardDescription>
              </CardHeader>
              <CardContent>
                {profile?.capability_statement_url ? (
                  <div className="flex items-center gap-4 mb-4 p-4 bg-green-50 rounded-lg">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Capability Statement Uploaded</p>
                      <a 
                        href={`${API}${profile.capability_statement_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-600 hover:underline"
                      >
                        View Document
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400 mb-4">
                    <FileText className="w-12 h-12 mx-auto mb-2" />
                    No capability statement uploaded
                  </div>
                )}
                <input 
                  type="file" 
                  accept=".pdf"
                  onChange={(e) => e.target.files[0] && uploadFile('capability', e.target.files[0])}
                  className="hidden"
                  id="capability-upload"
                />
                <label htmlFor="capability-upload">
                  <Button asChild variant="outline" className="w-full">
                    <span><Upload className="w-4 h-4 mr-2" /> Upload PDF</span>
                  </Button>
                </label>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Certifications Tab */}
        <TabsContent value="certifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" /> Certifications & Licenses
              </CardTitle>
              <CardDescription>
                Add your TX licenses, ISO certifications, and other credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6">
                {profile?.certifications?.map((cert) => (
                  <div key={cert.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {cert.verified ? (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      ) : (
                        <Award className="w-6 h-6 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium">{cert.name}</p>
                        <p className="text-sm text-gray-500">
                          {cert.issuing_body && `${cert.issuing_body} • `}
                          {cert.expiry_date && `Expires: ${cert.expiry_date}`}
                        </p>
                      </div>
                      {cert.verified && (
                        <Badge className="bg-green-100 text-green-800">Verified</Badge>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeCertification(cert.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}

                {(!profile?.certifications || profile.certifications.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No certifications added yet
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Add Certification</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input 
                    value={newCert.name}
                    onChange={(e) => setNewCert({...newCert, name: e.target.value})}
                    placeholder="Certification Name *"
                  />
                  <Input 
                    value={newCert.issuing_body}
                    onChange={(e) => setNewCert({...newCert, issuing_body: e.target.value})}
                    placeholder="Issuing Body"
                  />
                  <Input 
                    type="date"
                    value={newCert.expiry_date}
                    onChange={(e) => setNewCert({...newCert, expiry_date: e.target.value})}
                    placeholder="Expiry Date"
                  />
                </div>
                <Button onClick={addCertification} className="mt-3">
                  <Plus className="w-4 h-4 mr-2" /> Add Certification
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" /> Portfolio Gallery
              </CardTitle>
              <CardDescription>
                Showcase your warehouse, fleet, team, and completed projects
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {profile?.portfolio_gallery?.map((item) => (
                  <div key={item.id} className="relative group">
                    <img 
                      src={`${API}${item.image_url}`}
                      alt={item.title}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <div className="text-white text-center p-2">
                        <p className="font-medium text-sm">{item.title}</p>
                        <Badge variant="secondary" className="mt-1">{item.category}</Badge>
                      </div>
                    </div>
                  </div>
                ))}

                {(!profile?.portfolio_gallery || profile.portfolio_gallery.length === 0) && (
                  <div className="col-span-4 text-center py-8 text-gray-500">
                    No portfolio items yet. Add photos of your facilities, fleet, or completed work.
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-3">
                  Upload photos through the API endpoint. Categories: warehouse, fleet, team, project, equipment
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
